/**
 * Date utility functions
 */

import { DateRange } from '@/components/Molecules/FaceliftDatePicker';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

// Set default timezone to EST (America/New_York)
dayjs.tz.setDefault('America/New_York');

/**
 * Parse MM/DD/YYYY string to Date object (in NY timezone)
 */
export const parseStringToDate = (dateString: string): Date | undefined => {
	if (!dateString?.trim()) return undefined;

	try {
		const [month, day, year] = dateString.split('/');

		if (
			month &&
			day &&
			year &&
			month.length <= 2 &&
			day.length <= 2 &&
			year.length === 4
		) {
			// Create date in NY timezone using dayjs
			const date = dayjs
				.tz(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`, 'America/New_York')
				.startOf('day')
				.toDate();

			// Validate the date by checking if it matches the input (in NY timezone)
			const nyDate = dayjs(date).tz('America/New_York');
			if (
				nyDate.year() === parseInt(year, 10) &&
				nyDate.month() === parseInt(month, 10) - 1 &&
				nyDate.date() === parseInt(day, 10)
			) {
				return date;
			}
		}
	} catch (parseError) {
		console.warn('Invalid date format:', dateString, parseError);
	}

	return undefined;
};

/**
 * Format Date object to MM/DD/YYYY string (in NY timezone)
 */
export const formatDateToString = (date: Date): string => {
	if (!date || !(date instanceof Date) || Number.isNaN(date.getTime()))
		return '';

	// Convert to NY timezone and format
	const nyDate = dayjs(date).tz('America/New_York');
	const month = (nyDate.month() + 1).toString().padStart(2, '0');
	const day = nyDate.date().toString().padStart(2, '0');
	const year = nyDate.year().toString();

	return `${month}/${day}/${year}`;
};

/**
 * Auto-complete and auto-correct partial date input with intelligent validation
 * Examples:
 * - "1" -> "01/01/2024"
 * - "12/25" -> "12/25/2024"
 * - "44/44/2020" -> "04/04/2020" (corrects invalid month/day)
 * - "1144" -> "11/04/2024" (interprets as MM/DD)
 * - "444" -> "04/04/2044" (pads year)
 */
export const autoCompleteDate = (inputValue: string): string => {
	const now = dayjs().tz('America/New_York');
	const currentYear = now.year();

	// Remove any non-numeric characters except '/'
	const cleanValue = inputValue.replace(/[^\d/]/g, '');

	// If no slashes, try to interpret as continuous digits
	if (!cleanValue.includes('/') && cleanValue.length > 2) {
		return handleContinuousDigits(cleanValue, currentYear);
	}

	const parts = cleanValue.split('/');

	// Single part: month only
	if (parts.length === 1 && parts[0].length > 0) {
		const month = correctMonth(parts[0]);
		return `${month}/01/${currentYear}`;
	}

	// Two parts: month and day
	if (parts.length === 2 && parts[0].length > 0 && parts[1].length > 0) {
		const month = correctMonth(parts[0]);
		const day = correctDay(parts[1], parseInt(month));
		return `${month}/${day}/${currentYear}`;
	}

	// Three parts: month, day, and year
	if (
		parts.length === 3 &&
		parts[0].length > 0 &&
		parts[1].length > 0 &&
		parts[2].length > 0
	) {
		const month = correctMonth(parts[0]);
		const day = correctDay(parts[1], parseInt(month));
		const year = correctYear(parts[2], currentYear);
		return `${month}/${day}/${year}`;
	}

	// Return as-is if doesn't match patterns
	return inputValue;
};

/**
 * Handle continuous digits without slashes (e.g., "1144", "444", "12252024")
 */
const handleContinuousDigits = (
	digits: string,
	currentYear: number,
): string => {
	const len = digits.length;

	// Use lookup table for common patterns
	const patterns: Record<number, () => string> = {
		3: () => {
			const month = correctMonth(digits[0]);
			const day = correctDay(digits.slice(1), parseInt(month));
			return `${month}/${day}/20${digits.slice(0, 2)}`;
		},
		4: () => {
			const month = correctMonth(digits.slice(0, 2));
			const day = correctDay(digits.slice(2), parseInt(month));
			return `${month}/${day}/${currentYear}`;
		},
		6: () => {
			const month = correctMonth(digits.slice(0, 2));
			const day = correctDay(digits.slice(2, 4), parseInt(month));
			const year = correctYear(digits.slice(4), currentYear);
			return `${month}/${day}/${year}`;
		},
		8: () => {
			const month = correctMonth(digits.slice(0, 2));
			const day = correctDay(digits.slice(2, 4), parseInt(month));
			return `${month}/${day}/${digits.slice(4)}`;
		},
	};

	// Use pattern if available
	if (patterns[len]) {
		return patterns[len]();
	}

	// Handle longer patterns with potential year at end
	if (len > 4) {
		const potentialYear = digits.slice(-4);
		const yearNum = parseInt(potentialYear);

		if (yearNum >= 1900 && yearNum < 2100) {
			const monthDay = digits.slice(0, -4);
			if (monthDay.length >= 2) {
				const monthLen = Math.min(2, monthDay.length);
				const month = correctMonth(monthDay.slice(0, monthLen));
				const day = correctDay(monthDay.slice(monthLen), parseInt(month));
				return `${month}/${day}/${potentialYear}`;
			}
		}
	}

	// Fallback
	const monthLen = Math.min(2, len);
	const month = correctMonth(digits.slice(0, monthLen));
	const day = correctDay(digits.slice(monthLen), parseInt(month));
	return `${month}/${day}/${currentYear}`;
};

/**
 * Correct month value (1-12), handling invalid values
 */
const correctMonth = (monthStr: string): string => {
	const month = parseInt(monthStr) || 1;

	// Handle invalid months efficiently
	if (month <= 12) {
		return Math.max(1, month).toString().padStart(2, '0');
	}

	// Smart correction for 2-digit invalid months (e.g., 44 -> 04)
	if (month < 100 && monthStr.length === 2) {
		const secondDigit = month % 10;
		if (secondDigit >= 1 && secondDigit <= 12) {
			return secondDigit.toString().padStart(2, '0');
		}
	}

	// Default to December for other invalid cases
	return '12';
};

// Cache for days in month to avoid repeated Date calculations
const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]; // Using leap year February

/**
 * Correct day value based on month, handling invalid values
 */
const correctDay = (dayStr: string, month: number): string => {
	const day = parseInt(dayStr) || 1;
	const daysInMonth = DAYS_IN_MONTH[month - 1] || 31;

	// Handle valid days efficiently
	if (day <= daysInMonth) {
		return Math.max(1, day).toString().padStart(2, '0');
	}

	// Smart correction for 2-digit invalid days (e.g., 44 -> 04)
	if (day < 100 && dayStr.length === 2) {
		const secondDigit = day % 10;
		if (secondDigit >= 1 && secondDigit <= daysInMonth) {
			return secondDigit.toString().padStart(2, '0');
		}
	}

	// Default to last day of month for other invalid cases
	return daysInMonth.toString().padStart(2, '0');
};

/**
 * Correct year value, handling 2-digit years and padding
 */
const correctYear = (yearStr: string, currentYear: number): string => {
	const year = parseInt(yearStr);
	const len = yearStr.length;

	// Use lookup table for year length handling
	const yearHandlers: Record<number, () => number> = {
		1: () => 2000 + year,
		2: () => (year <= 30 ? 2000 + year : 1900 + year),
		3: () => (year < 100 ? 2000 + year : parseInt(`2${yearStr}`)),
	};

	return yearHandlers[len] ? yearHandlers[len]().toString() : year.toString();
};

/**
 * Validate if a date string matches MM/DD/YYYY format and is a valid date
 */
export const isValidDateString = (dateString: string): boolean => {
	return parseStringToDate(dateString) !== undefined;
};

/**
 * Filter input to allow only numbers and forward slashes
 */
export const filterDateInput = (value: string): string => {
	return value.replace(/[^0-9/]/g, '');
};

/**
 * Format input with automatic slash insertion for MM/DD/YYYY pattern
 * Examples: "12" -> "12/", "1234" -> "12/34", "12345678" -> "12/34/5678"
 */
export const formatInputWithAutoSlash = (value: string): string => {
	const numbersOnly = value.replace(/[^\d]/g, '');
	if (!numbersOnly) return '';

	const parts = [];
	const len = numbersOnly.length;

	// Month (first 2 digits)
	parts.push(numbersOnly.slice(0, 2));

	// Day (next 2 digits)
	if (len >= 3) {
		parts.push(numbersOnly.slice(2, 4));
	}

	// Year (remaining digits, max 4)
	if (len >= 5) {
		parts.push(numbersOnly.slice(4, 8));
	}

	return parts.join('/');
};

/**
 * Handle cursor position after auto-formatting
 * This ensures the cursor stays in the right position when slashes are auto-inserted
 */
export const getAdjustedCursorPosition = (
	oldValue: string,
	newValue: string,
	oldCursorPosition: number,
): number => {
	const numbersBeforeCursor = oldValue
		.slice(0, oldCursorPosition)
		.replace(/[^\d]/g, '').length;

	let digitCount = 0;
	for (let i = 0; i < newValue.length; i++) {
		if (/\d/.test(newValue[i])) {
			if (++digitCount === numbersBeforeCursor) {
				return Math.min(i + 1, newValue.length);
			}
		}
	}

	return newValue.length;
};

/**
 * Check if two dates are the same day (in NY timezone)
 */
export const isSameDay = (date1: Date, date2: Date): boolean => {
	const d1 = dayjs(date1).tz('America/New_York').startOf('day');
	const d2 = dayjs(date2).tz('America/New_York').startOf('day');
	return d1.isSame(d2, 'day');
};

/**
 * Check if a date is today (in NY timezone)
 */
export const isToday = (date: Date): boolean => {
	const today = dayjs().tz('America/New_York').startOf('day').toDate();
	const dateInNY = dayjs(date).tz('America/New_York').startOf('day').toDate();
	return isSameDay(dateInNY, today);
};

/**
 * Parse date range string to individual dates
 * Expected format: "MM/DD/YYYY - MM/DD/YYYY"
 */
export const parseDateRangeString = (
	rangeString: string,
): { startDate?: Date; endDate?: Date } => {
	if (!rangeString?.trim()) return {};

	const [start, end] = rangeString.split(' - ');
	return {
		startDate: start ? parseStringToDate(start.trim()) : undefined,
		endDate: end ? parseStringToDate(end.trim()) : undefined,
	};
};

/**
 * Format date range to string
 * Format: "MM/DD/YYYY - MM/DD/YYYY"
 */
export const formatDateRangeToString = (
	startDate?: Date,
	endDate?: Date,
): string => {
	const start = startDate ? formatDateToString(startDate) : '';
	const end = endDate ? formatDateToString(endDate) : '';

	return start && end ? `${start} - ${end}` : start || end || '';
};

/**
 * Validate if a date range is valid (end date >= start date)
 */
export const isValidDateRange = (startDate?: Date, endDate?: Date): boolean => {
	if (!startDate || !endDate) return true; // Partial ranges are valid
	return endDate >= startDate;
};

/**
 * Check if a date falls within a range (inclusive)
 */
export const isDateInRange = (
	date: Date,
	startDate?: Date,
	endDate?: Date,
): boolean => {
	if (!startDate || !endDate) return false;
	return date >= startDate && date <= endDate;
};

/**
 * Get number of days between two dates
 */
export const getDaysBetweenDates = (startDate: Date, endDate: Date): number => {
	const timeDiff = endDate.getTime() - startDate.getTime();
	return Math.ceil(timeDiff / (1000 * 3600 * 24));
};

/**
 * Get current date in YYYY-MM-DD format (EST timezone)
 */
export const getCurrentDate = () => {
	return dayjs().tz('America/New_York').format('YYYY-MM-DD');
};

/**
 * Universal date formatter that handles multiple input types using Day.js
 * Converts any date format (ISO string, Date object, SQL date, MM/DD/YYYY, etc.) to MM/DD/YYYY
 *
 * For dates with time components (ISO strings), extracts the date part without timezone conversion
 * to prevent off-by-one-day errors (e.g., "2025-10-02T00:00:00Z" displays as "10/02/2025")
 *
 * @param dateInput - Can be a Date object, ISO string, SQL date string, or MM/DD/YYYY string
 * @param fallback - Optional fallback value if date is invalid (default: '-')
 * @returns Formatted date string in MM/DD/YYYY format or fallback value
 *
 * @example
 * formatDate('2025-10-02T00:00:00Z') // '10/02/2025' (uses date part, no timezone shift)
 * formatDate('2024-10-23') // '10/23/2024'
 * formatDate(new Date()) // '10/23/2024' (converted to EST)
 * formatDate('10/23/2024') // '10/23/2024'
 * formatDate(null) // '-'
 * formatDate(undefined, 'N/A') // 'N/A'
 */
export const formatDate = (
	dateInput: string | Date | null | undefined,
	fallback: string = '-',
): string => {
	// Handle null, undefined, or empty string
	if (!dateInput) return fallback;

	try {
		// If it's a string
		if (typeof dateInput === 'string') {
			const trimmed = dateInput.trim();

			// Empty string
			if (!trimmed) return fallback;

			// Already in MM/DD/YYYY format - validate and return as-is
			if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
				const parsed = dayjs(trimmed, 'MM/DD/YYYY', true);
				if (parsed.isValid()) {
					return parsed.format('MM/DD/YYYY');
				}
			}

			// For ISO strings with time (e.g., "2025-10-02T00:00:00Z" or "2025-10-02T00:00:00")
			// Extract date part to avoid timezone conversion issues
			if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
				const datePart = trimmed.split('T')[0]; // Extract "2025-10-02"
				const parsed = dayjs(datePart, 'YYYY-MM-DD', true);
				if (parsed.isValid()) {
					return parsed.format('MM/DD/YYYY');
				}
			}

			// For YYYY-MM-DD format (SQL date without time)
			if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
				const parsed = dayjs(trimmed, 'YYYY-MM-DD', true);
				if (parsed.isValid()) {
					return parsed.format('MM/DD/YYYY');
				}
			}

			// For other formats, try parsing
			const parsed = dayjs(trimmed);
			if (parsed.isValid()) {
				return parsed.format('MM/DD/YYYY');
			}
		}
		// If it's a Date object, convert to EST
		else if (dateInput instanceof Date) {
			const parsed = dayjs(dateInput).tz('America/New_York');
			if (parsed.isValid()) {
				return parsed.format('MM/DD/YYYY');
			}
		}

		// If we couldn't parse the date, return fallback
		return fallback;
	} catch (error) {
		console.warn('Error formatting date:', dateInput, error);
		return fallback;
	}
};

/**
 * Format date with time in MM/DD/YYYY hh:mm A format (EST timezone)
 *
 * @param dateInput - Can be a Date object, ISO string, or date string
 * @param fallback - Optional fallback value if date is invalid (default: '-')
 * @returns Formatted date string with time in MM/DD/YYYY hh:mm A format or fallback value
 *
 * @example
 * formatDateTime('2025-10-02T14:30:00Z') // '10/02/2025 02:30 PM'
 * formatDateTime('2024-10-23T09:15:00') // '10/23/2024 09:15 AM'
 * formatDateTime(new Date()) // '10/23/2024 03:45 PM' (converted to EST)
 * formatDateTime(null) // '-'
 */
export const formatDateTime = (
	dateInput: string | Date | null | undefined,
	fallback: string = '-',
): string => {
	// Handle null, undefined, or empty string
	if (!dateInput) return fallback;

	try {
		// If it's a string
		if (typeof dateInput === 'string') {
			const trimmed = dateInput.trim();

			// Empty string
			if (!trimmed) return fallback;

			// Parse the date and convert to EST timezone
			const parsed = dayjs(trimmed).tz('America/New_York');
			if (parsed.isValid()) {
				return parsed.format('MM/DD/YYYY hh:mm A');
			}
		}
		// If it's a Date object, convert to EST
		else if (dateInput instanceof Date) {
			const parsed = dayjs(dateInput).tz('America/New_York');
			if (parsed.isValid()) {
				return parsed.format('MM/DD/YYYY hh:mm A');
			}
		}

		// If we couldn't parse the date, return fallback
		return fallback;
	} catch (error) {
		console.warn('Error formatting date with time:', dateInput, error);
		return fallback;
	}
};

export const getDateRangeForNotifications = (
	selectedValue: string,
	type: 'date' | 'dateISO' = 'date',
) => {
	const now = dayjs().tz('America/New_York');
	let startDate = '';
	let endDate = '';

	// For ISO dates, use ISO format with timezone
	const format = type === 'dateISO' ? 'YYYY-MM-DDTHH:mm:ss[Z]' : 'YYYY-MM-DD';

	switch (selectedValue) {
		case 'today':
			if (type === 'dateISO') {
				startDate = now.utc().startOf('day').format(format);
				endDate = now.utc().endOf('day').format(format);
			} else {
				startDate = now.startOf('day').format('YYYY-MM-DD');
				endDate = now.endOf('day').format('YYYY-MM-DD');
			}
			break;
		case 'yesterday':
			if (type === 'dateISO') {
				startDate = now.subtract(1, 'day').utc().startOf('day').format(format);
				endDate = now.subtract(1, 'day').utc().endOf('day').format(format);
			} else {
				startDate = now.subtract(1, 'day').startOf('day').format('YYYY-MM-DD');
				endDate = now.subtract(1, 'day').endOf('day').format('YYYY-MM-DD');
			}
			break;
		case 'tomorrow':
			if (type === 'dateISO') {
				startDate = now.add(1, 'day').utc().startOf('day').format(format);
				endDate = now.add(1, 'day').utc().endOf('day').format(format);
			} else {
				startDate = now.add(1, 'day').startOf('day').format('YYYY-MM-DD');
				endDate = now.add(1, 'day').endOf('day').format('YYYY-MM-DD');
			}
			break;
		case 'this_week':
			if (type === 'dateISO') {
				startDate = now.utc().startOf('week').format(format);
				endDate = now.utc().endOf('week').format(format);
			} else {
				startDate = now.startOf('week').format('YYYY-MM-DD');
				endDate = now.endOf('week').format('YYYY-MM-DD');
			}
			break;
		case 'last_week':
			if (type === 'dateISO') {
				startDate = now
					.subtract(1, 'week')
					.utc()
					.startOf('week')
					.format(format);
				endDate = now.subtract(1, 'week').utc().endOf('week').format(format);
			} else {
				startDate = now
					.subtract(1, 'week')
					.startOf('week')
					.format('YYYY-MM-DD');
				endDate = now.subtract(1, 'week').endOf('week').format('YYYY-MM-DD');
			}
			break;
		case 'this_month':
			if (type === 'dateISO') {
				startDate = now.utc().startOf('month').format(format);
				endDate = now.utc().endOf('month').format(format);
			} else {
				startDate = now.startOf('month').format('YYYY-MM-DD');
				endDate = now.endOf('month').format('YYYY-MM-DD');
			}
			break;
		case 'last_month':
			if (type === 'dateISO') {
				startDate = now
					.subtract(1, 'month')
					.utc()
					.startOf('month')
					.format(format);
				endDate = now.subtract(1, 'month').utc().endOf('month').format(format);
			} else {
				startDate = now
					.subtract(1, 'month')
					.startOf('month')
					.format('YYYY-MM-DD');
				endDate = now.subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
			}
			break;
		case 'this_year':
			if (type === 'dateISO') {
				startDate = now.utc().startOf('year').format(format);
				endDate = now.utc().endOf('year').format(format);
			} else {
				startDate = now.startOf('year').format('YYYY-MM-DD');
				endDate = now.endOf('year').format('YYYY-MM-DD');
			}
			break;
		case 'last_year':
			if (type === 'dateISO') {
				startDate = now
					.subtract(1, 'year')
					.utc()
					.startOf('year')
					.format(format);
				endDate = now.subtract(1, 'year').utc().endOf('year').format(format);
			} else {
				startDate = now
					.subtract(1, 'year')
					.startOf('year')
					.format('YYYY-MM-DD');
				endDate = now.subtract(1, 'year').endOf('year').format('YYYY-MM-DD');
			}
			break;
		case 'year_to_date':
			if (type === 'dateISO') {
				startDate = now.utc().startOf('year').format(format);
				endDate = now.utc().format(format);
			} else {
				startDate = now.startOf('year').format('YYYY-MM-DD');
				endDate = now.format('YYYY-MM-DD');
			}
			break;
		default:
			return '';
	}

	return `${startDate} || ${endDate}`;
};

export const formatDateInUTC = (value: string) => {
	const [month, day, year] = value.split('/');

	const now = new Date();
	const hh = String(now.getHours()).padStart(2, '0');
	const mm = String(now.getMinutes()).padStart(2, '0');
	const ss = String(now.getSeconds()).padStart(2, '0');

	return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')} ${hh}:${mm}:${ss}`;
};

/**
 * Convert date from MM/DD/YYYY to YYYY-MM-DD format without timezone conversion
 * Handles both MM/DD/YYYY and YYYY-MM-DD formats
 * 
 * @param dateString - Date string in MM/DD/YYYY or YYYY-MM-DD format
 * @returns Date string in YYYY-MM-DD format or null if invalid
 * 
 * @example
 * convertToISODateFormat('01/25/2026') // '2026-01-25'
 * convertToISODateFormat('2026-01-25') // '2026-01-25'
 * convertToISODateFormat(null) // null
 */
export const convertToISODateFormat = (dateString: string | null | undefined): string | null => {
	if (!dateString) return null;
	
	const trimmed = dateString.trim();
	if (!trimmed) return null;
	
	// Already in YYYY-MM-DD format
	if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
		return trimmed;
	}
	
	// Convert MM/DD/YYYY to YYYY-MM-DD
	if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
		const [month, day, year] = trimmed.split('/');
		return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
	}
	
	return null;
};

/**
 * Get year-to-date date range for column filters
 */
export const getYearToDateRange = (dateISO = false, filterType = 'year_to_date'): string => {
	return getDateRangeForNotifications(filterType, dateISO ? 'dateISO' : 'date');
};

export const getDateRange = (option: string, date: DateRange, type: 'date' | 'dateISO' = 'date') => {
	const now = dayjs().tz('America/New_York');
	let startDate = '';
	let endDate = '';
	
	// For ISO dates, use ISO format with timezone
	const format = type === 'dateISO' ? 'YYYY-MM-DDTHH:mm:ss[Z]' : 'YYYY-MM-DD';
	
	switch (option) {
		case 'on': {
			if (type === 'dateISO') {
				// Parse date parts and construct UTC date explicitly
				const [month, day, year] = date.startDate?.split('/').map(Number) || [0, 0, 0];
				// Create date at midnight UTC
				const utcDate = dayjs.utc().year(year).month(month - 1).date(day).hour(0).minute(0).second(0).millisecond(0);
				startDate = utcDate.format(format);
				endDate = utcDate.hour(23).minute(59).second(59).format(format);
			} else {
				const parsedStartDate = dayjs(date.startDate, 'MM/DD/YYYY');
				const formattedStartDate = parsedStartDate.format(format);
				const parsedEndDate = dayjs(date.endDate, 'MM/DD/YYYY');
				const formattedEndDate = parsedEndDate.format(format);
				return {startDate: formattedStartDate, endDate: formattedEndDate};
			}
			return {startDate, endDate};
		}
		case 'today':
			if (type === 'dateISO') {
				return {startDate: now.utc().startOf('day').format(format), endDate: now.utc().endOf('day').format(format)};
			} else {
				return {startDate: now.startOf('day').format('YYYY-MM-DD'), endDate: now.endOf('day').format('YYYY-MM-DD')};
			}
		case 'yesterday':
			if (type === 'dateISO') {
				return {startDate: now.subtract(1, 'day').utc().startOf('day').format(format), endDate: now.subtract(1, 'day').utc().endOf('day').format(format)};
			} else {
				return {startDate: now.subtract(1, 'day').startOf('day').format('YYYY-MM-DD'), endDate: now.subtract(1, 'day').endOf('day').format('YYYY-MM-DD')};
			}
		case 'tomorrow':
			if (type === 'dateISO') {
				return {startDate: now.add(1, 'day').utc().startOf('day').format(format), endDate: now.add(1, 'day').utc().endOf('day').format(format)};
			} else {
				return {startDate: now.add(1, 'day').startOf('day').format('YYYY-MM-DD'), endDate: now.add(1, 'day').endOf('day').format('YYYY-MM-DD')};
			}
		case 'this_week':
			if (type === 'dateISO') {
				return {startDate: now.utc().startOf('week').format(format), endDate: now.utc().endOf('week').format(format)};
			} else {
				return {startDate: now.startOf('week').format('YYYY-MM-DD'), endDate: now.endOf('week').format('YYYY-MM-DD')};
			}
		case 'last_week':
			if (type === 'dateISO') {
				return {startDate: now.subtract(1, 'week').utc().startOf('week').format(format), endDate: now.subtract(1, 'week').utc().endOf('week').format(format)};
			} else {
				return {startDate: now.subtract(1, 'week').startOf('week').format('YYYY-MM-DD'), endDate: now.subtract(1, 'week').endOf('week').format('YYYY-MM-DD')};
			}
		case 'this_month':
			if (type === 'dateISO') {
				return {startDate: now.utc().startOf('month').format(format), endDate: now.utc().endOf('month').format(format)};
			} else {
				return {startDate: now.startOf('month').format('YYYY-MM-DD'), endDate: now.endOf('month').format('YYYY-MM-DD')};
			}
		case 'last_month':
			if (type === 'dateISO') {
				return {startDate: now.subtract(1, 'month').utc().startOf('month').format(format), endDate: now.subtract(1, 'month').utc().endOf('month').format(format)};
			} else {
				return {startDate: now.subtract(1, 'month').startOf('month').format('YYYY-MM-DD'), endDate: now.subtract(1, 'month').endOf('month').format('YYYY-MM-DD')};
			}
		case 'this_year':
			if (type === 'dateISO') {
				return {startDate: now.utc().startOf('year').format(format), endDate: now.utc().endOf('year').format(format)};
			} else {
				return {startDate: now.startOf('year').format('YYYY-MM-DD'), endDate: now.endOf('year').format('YYYY-MM-DD')};
			}
		case 'last_year':
			if (type === 'dateISO') {
				return {startDate: now.subtract(1, 'year').utc().startOf('year').format(format), endDate: now.subtract(1, 'year').utc().endOf('year').format(format)};
			} else {
				return {startDate: now.subtract(1, 'year').startOf('year').format('YYYY-MM-DD'), endDate: now.subtract(1, 'year').endOf('year').format('YYYY-MM-DD')};
			}
		case 'year_to_date':
			if (type === 'dateISO') {
				return {startDate: now.utc().startOf('year').format(format), endDate: now.utc().format(format)};
			} else {
				return {startDate: now.startOf('year').format('YYYY-MM-DD'), endDate: now.format('YYYY-MM-DD')};
			}
		case 'last_12_months':
			if (type === 'dateISO') {
				return {startDate: now.utc().subtract(12, 'month').startOf('month').format(format), endDate: now.utc().format(format)};
			} else {
				return {startDate: now.subtract(12, 'month').startOf('month').format('YYYY-MM-DD'), endDate: now.format('YYYY-MM-DD')};
			}
		default:
			return {startDate: '', endDate: ''};
	}
};